import type {
  ActionResponse, AdminStatsResponse, GameInfoResponse, HistoryResponse,
  JoinResponse, OpenGame, OpenGamesResponse, RegisterResponse, SeatConfig,
} from '../shared/types';
import { api, el, showError, showScreen } from './api';
import { hideRoundOverlay, renderGame, renderHistory, resetRenderCache } from './render';
import {
  apply, currentGameId, isAdmin, playerId, playerName, readResumeTarget,
  setGame, setPlayer, viewerSeat,
} from './store';
import { connect } from './sse';
import { leaveGame } from './actions';

export function applyAdminUI(): void {
  el('admin-btn').classList.toggle('hidden', !isAdmin);
}

export function goToSetupScreen(): void {
  showScreen('setup-screen');
}

export function goToJoinScreen(): void {
  showScreen('join-screen');
  el<HTMLInputElement>('join-code-input').value = '';
  void loadOpenGames();
}

export async function loadOpenGames(): Promise<void> {
  try {
    const data = await api<OpenGamesResponse>('GET', '/games/open');
    renderOpenGames(data.games, 'open-games-list');
  } catch { /* ignore */ }
}

export async function loadOpenGamesMenu(): Promise<void> {
  try {
    const data = await api<OpenGamesResponse>('GET', '/games/open');
    renderOpenGames(data.games, 'menu-open-games-list');
  } catch { /* ignore */ }
}

function renderOpenGames(games: OpenGame[], listId = 'open-games-list'): void {
  const list = el(listId);
  if (games.length === 0) {
    list.innerHTML = '<div class="no-open-games">No open tables right now</div>';
    return;
  }
  list.innerHTML = games.map(g => `
    <div class="open-game-card">
      <div class="open-game-info">
        <span class="open-game-creator">${g.creatorName}'s table</span>
        <span class="open-game-seats">${g.humanSeatsFilled}/${g.humanSeatsTotal} joined</span>
      </div>
      <button class="btn btn-sm btn-join-open" data-code="${g.joinCode}">Join</button>
    </div>
  `).join('');
  list.querySelectorAll<HTMLButtonElement>('.btn-join-open').forEach(btn => {
    btn.addEventListener('click', async () => {
      const code = btn.dataset.code ?? '';
      try {
        const info = await api<GameInfoResponse>('GET', `/game-info/${code}`);
        const available = info.players.filter(p => p.isHuman && !p.joined && p.index !== 0);
        if (available.length === 1) await doJoin(code, available[0].index);
        else showSeatPicker(info, code);
      } catch (e) {
        showError((e as Error).message);
      }
    });
  });
}

export function setupToggleListeners(): void {
  const updateOpenWarning = (): void => {
    const isOpen = el<HTMLInputElement>('table-open').checked;
    const hasHumanSeat = el<HTMLInputElement>('seat-west').checked
      || el<HTMLInputElement>('seat-north').checked
      || el<HTMLInputElement>('seat-east').checked;
    el('open-seats-warning').classList.toggle('hidden', !isOpen || hasHumanSeat);
  };
  const seats: Array<{ id: string; labelId: string }> = [
    { id: 'seat-north', labelId: 'north-label' },
    { id: 'seat-west', labelId: 'west-label' },
    { id: 'seat-east', labelId: 'east-label' },
  ];
  for (const { id, labelId } of seats) {
    el<HTMLInputElement>(id).addEventListener('change', (e) => {
      el(labelId).textContent = (e.target as HTMLInputElement).checked ? 'Human' : 'NPC';
      updateOpenWarning();
    });
  }
  el<HTMLInputElement>('table-open').addEventListener('change', (e) => {
    const open = (e.target as HTMLInputElement).checked;
    el('open-label').textContent = open ? 'Open Table 🌐' : 'Closed Table 🔒';
    el('visibility-hint').textContent = open
      ? 'Anyone can find and join this table'
      : 'Players need your code to join';
    updateOpenWarning();
  });
}

export async function handleCreateGame(): Promise<void> {
  if (!playerId) return;
  const seatConfig: SeatConfig = {
    west: el<HTMLInputElement>('seat-west').checked,
    north: el<HTMLInputElement>('seat-north').checked,
    east: el<HTMLInputElement>('seat-east').checked,
  };
  const isOpen = el<HTMLInputElement>('table-open').checked;
  const winScore = parseInt((el('win-score-select') as HTMLSelectElement).value, 10);
  try {
    const data = await api<ActionResponse>('POST', '/game', {
      playerId, playerName, seatConfig, isOpen, winScore,
    });
    enterTable(data.gameState.gameId, 0, data);
  } catch (e) {
    showError((e as Error).message);
  }
}

export async function handleJoin(): Promise<void> {
  if (!playerId) return;
  const code = el<HTMLInputElement>('join-code-input').value.trim().toUpperCase();
  if (code.length !== 6) {
    showError('Please enter the full 6-character code');
    return;
  }
  try {
    const info = await api<GameInfoResponse>('GET', `/game-info/${code}`);
    const availableSeats = info.players.filter(p => p.isHuman && !p.joined && p.index !== 0);
    if (availableSeats.length === 1) await doJoin(code, availableSeats[0].index);
    else showSeatPicker(info, code);
  } catch (e) {
    showError((e as Error).message);
  }
}

function showSeatPicker(info: GameInfoResponse, code: string): void {
  const SEAT_NAMES = ['South', 'West', 'North', 'East'];
  const table = el('seat-picker-table');
  const slots = [
    { index: 2, pos: 'sp-north' },
    { index: 1, pos: 'sp-west' },
    { index: 0, pos: 'sp-south' },
    { index: 3, pos: 'sp-east' },
  ];
  table.innerHTML = slots.map(({ index, pos }) => {
    const p = info.players[index];
    const isAvailable = p.isHuman && !p.joined && index !== 0;
    const isCreator = index === 0;
    const teamLabel = (index === 0 || index === 2) ? 'Your team' : 'Opponents';
    const teamClass = (index === 0 || index === 2) ? 'sp-team-us' : 'sp-team-them';
    if (isAvailable) {
      return `
        <button class="sp-slot sp-available ${pos}" data-seat="${index}">
          <div class="sp-seat-name">${SEAT_NAMES[index]}</div>
          <div class="sp-team-badge ${teamClass}">${teamLabel}</div>
          <div class="sp-join-label">Sit Here</div>
        </button>`;
    }
    const label = isCreator ? `${p.name} 👑` : (p.isHuman ? p.name : 'NPC');
    return `
        <div class="sp-slot sp-occupied ${pos}">
          <div class="sp-seat-name">${SEAT_NAMES[index]}</div>
          <div class="sp-team-badge ${teamClass}">${teamLabel}</div>
          <div class="sp-player-name">${label}</div>
        </div>`;
  }).join('');
  table.querySelectorAll<HTMLButtonElement>('.sp-available').forEach(btn => {
    btn.addEventListener('click', async () => {
      el('seat-picker-modal').classList.add('hidden');
      await doJoin(code, parseInt(btn.dataset.seat ?? '1', 10));
    });
  });
  el('seat-picker-modal').classList.remove('hidden');
}

async function doJoin(code: string, seat: number): Promise<void> {
  if (!playerId) return;
  try {
    const data = await api<JoinResponse>('POST', `/join/${code}`, { playerId, playerName, seat });
    enterTable(data.gameId, data.viewerSeat, data);
  } catch (e) {
    showError((e as Error).message);
  }
}

function enterTable(
  gameId: string,
  seat: number,
  data: { seq: number; gameState: ActionResponse['gameState']; events?: ActionResponse['events'] },
): void {
  setGame(gameId, seat);
  hideRoundOverlay();
  resetRenderCache();
  showScreen('game-screen');
  if (playerId) connect(gameId, playerId);
  apply({ seq: data.seq, gameState: data.gameState, events: data.events ?? [] });
}

export async function tryResumeGame(): Promise<boolean> {
  if (!playerId) return false;
  const target = readResumeTarget();
  if (!target) return false;
  try {
    const data = await api<{ seq: number; gameState: ActionResponse['gameState'] }>(
      'GET',
      `/game/${target.gameId}?playerId=${playerId}`,
    );
    const gs = data.gameState;
    if (!gs || gs.phase === 'gameOver') {
      setGame(null, 0);
      return false;
    }
    setGame(gs.gameId, gs.viewerSeat ?? target.viewerSeat ?? 0);
    hideRoundOverlay();
    resetRenderCache();
    showScreen('game-screen');
    connect(gs.gameId, playerId);
    apply({ seq: data.seq ?? 0, gameState: gs, events: [] });
    return true;
  } catch {
    setGame(null, 0);
    return false;
  }
}

export function showBugModal(): void {
  el<HTMLTextAreaElement>('bug-message').value = '';
  el('bug-feedback').classList.add('hidden');
  el('bug-modal').classList.remove('hidden');
}

export async function handleBugSubmit(): Promise<void> {
  const message = el<HTMLTextAreaElement>('bug-message').value.trim();
  if (!message) return;
  try {
    await api('POST', '/bugs', { playerId, gameId: currentGameId, message });
    const fb = el('bug-feedback');
    fb.textContent = '✓ Thanks! Bug reported.';
    fb.classList.remove('hidden');
    setTimeout(() => el('bug-modal').classList.add('hidden'), 1500);
  } catch (e) {
    const fb = el('bug-feedback');
    fb.textContent = (e as Error).message;
    fb.classList.remove('hidden');
  }
}

export async function showHistory(): Promise<void> {
  if (!playerId) return;
  try {
    const data = await api<HistoryResponse>('GET', `/player/${playerId}/history`);
    renderHistory(data);
    el('history-modal').classList.remove('hidden');
  } catch (e) {
    showError((e as Error).message);
  }
}

export async function showAdminPanel(): Promise<void> {
  if (!playerId || !isAdmin) return;
  el('admin-content').innerHTML = '<p style="color:rgba(255,255,255,0.5);padding:16px">Loading…</p>';
  el('admin-summary').innerHTML = '';
  el('admin-modal').classList.remove('hidden');
  try {
    const data = await api<AdminStatsResponse>('GET', `/admin/players?adminId=${playerId}`);
    el('admin-summary').innerHTML = `
      <div class="admin-stats-row">
        <div class="admin-stat-box"><span class="admin-stat-num">${data.totalUsers}</span><span class="admin-stat-label">Total Users</span></div>
        <div class="admin-stat-box"><span class="admin-stat-num">${data.totalGames}</span><span class="admin-stat-label">Games Played</span></div>
        <div class="admin-stat-box"><span class="admin-stat-num">${data.totalTimesPlayed}</span><span class="admin-stat-label">Times Played</span></div>
      </div>
    `;
    if (data.players.length === 0) {
      el('admin-content').innerHTML = '<p style="color:rgba(255,255,255,0.4);padding:16px">No players yet.</p>';
      return;
    }
    const rows = data.players.map(p => {
      const rate = p.gamesPlayed > 0 ? Math.round((p.wins / p.gamesPlayed) * 100) : 0;
      const adminBadge = p.isAdmin ? ' <span class="admin-badge">ADMIN</span>' : '';
      return `
        <tr>
          <td>${p.name}${adminBadge}</td>
          <td class="admin-num">${p.gamesPlayed}</td>
          <td class="admin-num admin-win">${p.wins}</td>
          <td class="admin-num admin-loss">${p.losses}</td>
          <td class="admin-num">${rate}%</td>
        </tr>`;
    }).join('');
    el('admin-content').innerHTML = `
      <div class="admin-table-wrap">
        <table class="admin-table">
          <thead>
            <tr><th>Player</th><th>Played</th><th>Wins</th><th>Losses</th><th>Win %</th></tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `;
  } catch (e) {
    el('admin-content').innerHTML = `<p style="color:#f87171;padding:16px">${(e as Error).message}</p>`;
  }
}

/**
 * auth-v2.js is a module script, so it has run (and set the global) before
 * DOMContentLoaded unless it failed to load at all. Missing here means we
 * wait for its ready event once, with a single guard so a script that never
 * arrives still lands on the login screen. No interval: nothing to poll for.
 */
export async function waitForGodbeyAuth(): Promise<any> {
  if ((window as any).__godbeyAuth) return (window as any).__godbeyAuth;
  return new Promise((resolve) => {
    const done = (): void => {
      window.clearTimeout(guard);
      window.removeEventListener('godbey-auth-ready', done);
      resolve((window as any).__godbeyAuth ?? null);
    };
    const guard = window.setTimeout(done, 5000);
    window.addEventListener('godbey-auth-ready', done);
  });
}

export async function loginFromSupabase(): Promise<void> {
  const auth = await waitForGodbeyAuth();
  if (!auth) {
    setPlayer(null, '', false);
    showScreen('login-screen');
    return;
  }
  const user = await auth.getUser();
  if (!user) {
    setPlayer(null, '', false);
    showScreen('login-screen');
    return;
  }
  const previousPlayerId = playerId;
  const activeScreen = document.querySelector<HTMLElement>('.screen:not(.hidden)')?.id;
  try {
    const data = await api<RegisterResponse>('POST', '/player', {});
    setPlayer(data.playerId, data.name, data.isAdmin ?? false);
    applyAdminUI();
    el('menu-player-name').textContent = `Welcome, ${data.name}!`;
    const inGame = activeScreen === 'game-screen';
    const playerIdUnchanged = previousPlayerId !== null && data.playerId === previousPlayerId;
    if (!(inGame && playerIdUnchanged) && !readResumeTarget()) {
      showScreen('menu-screen');
      void loadOpenGamesMenu();
    }
  } catch (e) {
    console.error('Spades auto-login failed:', e);
    showScreen('login-screen');
  }
}

export async function handleLogout(): Promise<void> {
  leaveGame();
  const auth = await waitForGodbeyAuth();
  if (auth) await auth.signOut();
}

export { viewerSeat };
